import aerixMarkdown from '../../aerix-docs.md?raw';
import beaconMarkdown from '../../beacon-docs.md?raw';
import orbitMarkdown from '../../orbit-docs.md?raw';

export type TrackId = 'aerix' | 'beacon' | 'orbit';
export type ProductId = TrackId;
export type Level = 'Operator' | 'Administrator' | 'Expert';

export interface DocHeading {
  id: string;
  title: string;
  depth: number;
}

export interface DocArticle {
  slug: string;
  title: string;
  summary: string;
  track: TrackId;
  product: ProductId;
  category: string;
  service?: string;
  level: Level;
  minutes: number;
  reviewed: string;
  sourcePaths: string[];
  markdown: string;
  sections: DocHeading[];
}

export interface Track {
  id: TrackId;
  title: string;
  shortTitle: string;
  description: string;
  outcome: string;
  source: string;
  audience: string;
  version?: string;
  markdown: string;
}

const reviewed = 'July 12, 2026';

export const tracks: Track[] = [
  {
    id: 'aerix',
    title: 'Aerix',
    shortTitle: 'Aerix',
    description: 'Edge web server, reverse proxy, TLS terminator, HTTP/3 endpoint, PHP gateway, cache, and TCP/UDP stream proxy.',
    outcome: 'Install, configure, harden, operate, and troubleshoot Aerix across static, proxied, PHP, cached, and stream workloads.',
    source: 'aerix-docs.md',
    audience: 'Web platform operators and application teams',
    version: '2.1.7',
    markdown: aerixMarkdown,
  },
  {
    id: 'beacon',
    title: 'Beacon',
    shortTitle: 'Beacon',
    description: 'Recursive DNS resolver for classic DNS, DNS over TLS, DNS over HTTPS, DNS over QUIC, DNSSEC validation, caching, ACLs, and rate limits.',
    outcome: 'Build and run Beacon as a production recursive resolver with transport, cache, DNSSEC, firewall, and operating controls understood.',
    source: 'beacon-docs.md',
    audience: 'DNS and network operations teams',
    markdown: beaconMarkdown,
  },
  {
    id: 'orbit',
    title: 'Orbit',
    shortTitle: 'Orbit',
    description: 'Authoritative DNS service with REST management, CLI administration, SQL-backed standalone or leader nodes, and snapshot-synced workers.',
    outcome: 'Operate Orbit in standalone mode or clustered leader/worker mode while managing zones, records, backups, APIs, and security boundaries.',
    source: 'orbit-docs.md',
    audience: 'Authoritative DNS operators and platform engineers',
    markdown: orbitMarkdown,
  },
];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[`'"]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const countWords = (value: string) => (value.match(/[A-Za-z0-9_'-]+/g) ?? []).length;

const plain = (value: string) =>
  value
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[#>*_\-|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const summaryFrom = (markdown: string, fallback: string) => {
  const paragraph = markdown
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .find((block) => block && !block.startsWith('```') && !block.startsWith('|') && !/^[-*]\s/.test(block) && !/^#{1,6}\s/.test(block));
  return (paragraph ? plain(paragraph) : fallback).slice(0, 220);
};

const categoryFor = (product: ProductId, title: string): string => {
  const normalized = title.toLowerCase();

  if (product === 'aerix') {
    if (/tls|http\/3|limits|protection|security|htaerix/.test(normalized)) return 'Security and hardening';
    if (/proxy|php|static|cache|stream|variables|rewrites|maps/.test(normalized)) return 'Traffic handling';
    if (/config|cli|runtime|repository|build|install/.test(normalized)) return 'Setup and configuration';
    if (/operations|maintenance|troubleshooting|logging/.test(normalized)) return 'Operations';
    return 'Overview';
  }

  if (product === 'beacon') {
    if (/tls|dnssec|access|rate|http\/3|quic|firewall|privileged/.test(normalized)) return 'Security and transport';
    if (/config|install|build|quick|runtime|repository/.test(normalized)) return 'Setup and configuration';
    if (/recursive|caching|dns transports|testing/.test(normalized)) return 'Resolver behavior';
    if (/operations|troubleshooting|constraints|development|logging/.test(normalized)) return 'Operations';
    return 'Overview';
  }

  if (/standalone|storage backends|quick start examples/.test(normalized)) return 'Standalone mode';
  if (/runtime modes|cluster|worker|leader|migration with workers/.test(normalized)) return 'Cluster mode';
  if (/api|cli/.test(normalized)) return 'API and CLI';
  if (/dns behavior|zones|records/.test(normalized)) return 'Authoritative DNS';
  if (/security|backup|metrics|logs|operations|troubleshooting|limitations/.test(normalized)) return 'Security and operations';
  if (/install|filesystem|config|architecture/.test(normalized)) return 'Setup and configuration';
  return 'Overview';
};

const levelFor = (title: string): Level => {
  const normalized = title.toLowerCase();
  if (/cluster|dnssec|http\/3|quic|security|proxy|cache|rest api|htaerix|stream|migration|maintenance/.test(normalized)) return 'Expert';
  if (/configuration|operations|troubleshooting|records|tls|limits|access|logging/.test(normalized)) return 'Administrator';
  return 'Operator';
};

const headingsFrom = (markdown: string): DocHeading[] =>
  markdown
    .split(/\r?\n/)
    .map((line) => line.match(/^(#{3,4})\s+(.+)$/))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map((match) => ({
      depth: match[1].length,
      title: match[2].replace(/`/g, ''),
      id: slugify(match[2]),
    }));

function extractArticles(product: Track): DocArticle[] {
  const lines = product.markdown.split(/\r?\n/);
  const result: DocArticle[] = [];
  let currentTitle = '';
  let currentLines: string[] = [];

  const flush = () => {
    if (!currentTitle || currentTitle.toLowerCase() === 'contents') return;
    const markdown = currentLines.join('\n').trim();
    if (!markdown) return;
    result.push({
      slug: `${product.id}-${slugify(currentTitle)}`,
      title: currentTitle,
      summary: summaryFrom(markdown, product.description),
      track: product.id,
      product: product.id,
      category: categoryFor(product.id, currentTitle),
      service: product.id,
      level: levelFor(currentTitle),
      minutes: Math.max(3, Math.ceil(countWords(markdown) / 225)),
      reviewed,
      sourcePaths: [product.source],
      markdown,
      sections: headingsFrom(markdown),
    });
  };

  for (const line of lines) {
    const heading = line.match(/^##\s+(.+)$/);
    if (heading) {
      flush();
      currentTitle = heading[1].replace(/`/g, '');
      currentLines = [];
      continue;
    }
    if (currentTitle && currentTitle.toLowerCase() !== 'contents') {
      currentLines.push(line);
    }
  }
  flush();
  return result;
}

export const articles: DocArticle[] = tracks.flatMap(extractArticles);

export const articleBySlug = new Map(articles.map((article) => [article.slug, article]));

export const articlesByTrack = tracks.reduce<Record<TrackId, DocArticle[]>>(
  (result, track) => {
    result[track.id] = articles.filter((article) => article.track === track.id);
    return result;
  },
  {
    aerix: [],
    beacon: [],
    orbit: [],
  },
);

export const serviceCategories = Array.from(new Set(articles.map((article) => article.category)));

export const categoriesByTrack = tracks.reduce<Record<TrackId, string[]>>(
  (result, track) => {
    result[track.id] = Array.from(new Set(articlesByTrack[track.id].map((article) => article.category)));
    return result;
  },
  {
    aerix: [],
    beacon: [],
    orbit: [],
  },
);

export const searchableText = (article: DocArticle) =>
  [
    article.title,
    article.summary,
    article.category,
    article.service,
    article.markdown,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
